import { useApi } from './useApi';
import { useAppState } from './useAppState';
import { getHttpInstance, getApiConfig } from './useApi';
export function useTransactions() {
    const api = useApi();
    const state = useAppState();
    // withMerchantRule appends getCustomerMerchantRule=true — only the transaction
    // detail page needs it. The query string is part of the cache-hash key, so
    // getTransaction and getCachedTransactionHash must be called with the same flag.
    const transactionPath = (id, withMerchantRule = false) => "/Customers/00000000-0000-0000-0000-000000000000/BankAccounts/Transactions/" + id + (withMerchantRule ? "?getCustomerMerchantRule=true" : "");
    function getTransaction(id, onSuccess = null, withMerchantRule = false) {
        // forceSetCache: always refetch from the network (no cache short-circuit)
        // but persist data + the server hash so callers can detect changes.
        api.GET(transactionPath(id, withMerchantRule), (result) => {
            state.transaction = result;
            if (onSuccess)
                onSuccess(state.transaction);
        }, false, true);
    }
    // The server content hash (x-mgcache-md5) for this transaction's last cached
    // response, or null if not cached. Changes whenever the payload changes.
    function getCachedTransactionHash(id, withMerchantRule = false) {
        return api.getCacheHash(api.cacheUrl(transactionPath(id, withMerchantRule)));
    }
    function getTransactions(classificationId, from, to, onSuccess = null, includeChildrenClassifications = false, refreshCache = false, includeExcludedFromAnalysis = true, staleWhileRevalidate = false, pollUntilChanged = false) {
        const todate = to.split('T')[0] + "T23:59:59";
        let uri = "/Customers/00000000-0000-0000-0000-000000000000/BankAccounts/Transactions?classificationId=" + classificationId + "&from=" + from + "&to=" + todate + "&includeChildrenClassifications=" + includeChildrenClassifications;
        if (!includeExcludedFromAnalysis) {
            uri += "&includeExcludedFromAnalysis=" + includeExcludedFromAnalysis;
        }
        // Poll fallback key: the set of returned transaction ids. Stable across the
        // volatile fields (balances, timestamps) so the poll only stops once the
        // uncategorised list itself actually changes server-side.
        const changeKey = (d) => ((d?.transactions ?? []).map((t) => t.id).sort().join(','));
        api.GET(uri, (result, isStale) => {
            state.transactions = result;
            if (onSuccess)
                onSuccess(result, isStale);
        }, !refreshCache, refreshCache, 3, false, staleWhileRevalidate, pollUntilChanged, changeKey);
    }
    function clearTransactionsCache() {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.includes('BankAccounts/Transactions?classificationId')) {
                localStorage.removeItem(key);
                i--;
            }
        }
    }
    function searchTransactions(search, onSuccess = null) {
        getHttpInstance().post(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/Transactions/TransactionSearches?search=" + search, null)
            .then((response) => { if (onSuccess)
            onSuccess(response.data); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => searchTransactions(search, onSuccess));
            }
        });
    }
    function getTransactionClassifications(onSuccess = null) {
        api.GET("/TransactionClassifications?lite=true&hierarchical=true", (result) => {
            state.transactionClassifications = result;
            if (onSuccess)
                onSuccess(state.transactionClassifications);
        }, true);
    }
    function updateTransactionClassification(transaction, onSuccess = null) {
        getHttpInstance().put(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/BankAccounts/Transactions/" + transaction.id, transaction)
            .then(() => { if (onSuccess)
            onSuccess(); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => updateTransactionClassification(transaction, onSuccess));
            }
        });
    }
    function updateTransactionClassificationMerchantRule(transaction, onSuccess = null) {
        getHttpInstance().post(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/BankAccounts/CustomerMerchantRules", transaction)
            .then(() => { if (onSuccess)
            onSuccess(); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => updateTransactionClassificationMerchantRule(transaction, onSuccess));
            }
        });
    }
    function updateBulkTransactionClassifications(transactions, selectedClassification, onSuccess = null) {
        const transactionIDs = transactions.value.map((t) => t.id);
        const body = { transactionIDs, newClassificationID: selectedClassification.id };
        getHttpInstance().post(getApiConfig().api + "/Customers/Transactions/BulkUntagged/BulkTagRequests?updateCustomerClassification=true", body)
            .then(() => { if (onSuccess)
            onSuccess(); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => updateBulkTransactionClassifications(transactions, selectedClassification, onSuccess));
            }
        });
    }
    function getSuggestedClassification(merchant, onSuccess = null, refreshCache = false) {
        const uri = "/Customers/Transactions/Vendors/ClassificationSuggestions?merchant=" + encodeURIComponent(merchant);
        api.GET(uri, (result) => {
            state.suggestedClassification = result;
            if (onSuccess)
                onSuccess(result);
        }, !refreshCache, refreshCache);
    }
    // Shared dispatcher for "change category" flows (single tx and bulk).
    // Routes to either the merchant-rule API (when "always apply" is on) or
    // the per-transaction PUT, dedupes by merchant in the rule branch, and
    // invokes `onAllDone` once every fired call has resolved.
    function applyCategoryToTransactions(transactions, payload, onAllDone = null) {
        const { classification, alwaysApply, excludeFromAnalysis, alwaysApplyExcludeInclude } = payload;
        if (!classification || !transactions.length) {
            if (onAllDone)
                onAllDone();
            return;
        }
        const runDirectUpdates = (txs) => {
            let remaining = txs.length;
            const onOneDone = () => {
                remaining--;
                if (remaining > 0)
                    return;
                if (onAllDone)
                    onAllDone();
            };
            for (const t of txs) {
                const updated = {
                    ...t,
                    customerClassification: classification,
                    effectiveClassification: classification,
                    excludeFromAnalysis,
                };
                updateTransactionClassification(updated, onOneDone);
            }
        };
        if (alwaysApply || alwaysApplyExcludeInclude) {
            // One merchant rule per unique merchant — applies server-side to every match.
            const byMerchant = new Map();
            for (const t of transactions) {
                const name = t.merchantName;
                if (!name || byMerchant.has(name))
                    continue;
                byMerchant.set(name, t);
            }
            // Fall through to direct updates if nothing has a merchant name.
            if (byMerchant.size === 0) {
                runDirectUpdates(transactions);
                return;
            }
            let remaining = byMerchant.size;
            const onOneDone = () => {
                remaining--;
                if (remaining > 0)
                    return;
                if (onAllDone)
                    onAllDone();
            };
            for (const t of byMerchant.values()) {
                const rulePayload = {
                    alwaysExcludeFromAnalysis: alwaysApplyExcludeInclude && excludeFromAnalysis,
                    merchantName: t.merchantName,
                    chosenTransactionClassification: {
                        mgClassification: classification.mgClassification,
                        mgSubClassification: classification.mgSubClassification,
                        grandParentClassification: classification.grandParentClassification,
                    },
                };
                updateTransactionClassificationMerchantRule(rulePayload, onOneDone);
            }
            return;
        }
        runDirectUpdates(transactions);
    }
    function updateTransactionTags(bankAccountId, transactionId, tags, onSuccess = null) {
        const transactionTags = tags.map((t) => t.id ? { "tag": t.tag, "id": t.id } : { "tag": t.tag || t });
        getHttpInstance().put(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/BankAccounts/" + bankAccountId + "/Transactions/" + transactionId + "/CustomerTags", transactionTags)
            .then(() => { if (onSuccess)
            onSuccess(); });
    }
    return {
        getTransaction,
        getCachedTransactionHash,
        getTransactions,
        clearTransactionsCache,
        searchTransactions,
        getTransactionClassifications,
        updateTransactionClassification,
        updateTransactionClassificationMerchantRule,
        updateBulkTransactionClassifications,
        applyCategoryToTransactions,
        getSuggestedClassification,
        updateTransactionTags,
    };
}
